import { CHARACTERS, ENEMIES, UPGRADES, type CharacterId, type EnemyType, type Mode, type State, type UpgradeId } from './config'
export interface Enemy { id: number; type: EnemyType; x: number; z: number; hp: number; maxHp: number; radius: number; speed: number; damage: number; exp: number; kx: number; kz: number; hitCd: number; flash: number; slow: number; stun: number; attack: number }
export interface Wave { id:number; x:number; z:number; radius:number; maxRadius:number; damage:number; delay:number; hits:Set<number> }
export interface Summon { id:number; kind:'mother'|'leopard'; x:number;z:number;life:number;attackCd:number;pose:number;facingX:number }
export interface Orb { id: number; x: number; z: number; value: number }
export interface GameEvent { type: 'coin' | 'dash' | 'attack' | 'hit' | 'kill' | 'hurt' | 'collect' | 'skill' | 'level' | 'boss' | 'won' | 'lost'; x: number; z: number; value?: number }
export interface Input { x: number; z: number; skill: boolean }
export const clamp = (x: number, a: number, b: number) => Math.max(a, Math.min(b, x))
export class Simulation {
  state: State = 'menu'
  character: CharacterId = 'cow'
  mode: Mode = 'trial'
  distance = 0; runCoins = 0;
  time = 0; kills = 0; level = 1; exp = 0; nextExp = 6; combo = 0; maxCombo = 0
  comboTimer = 0; spawnTimer = 0; nextBossAt = 90; id = 0; bossSpawned = false; bossDefeated = false
  input: Input = { x: 0, z: 0, skill: false }
  previousSkill = false
  player = this.createPlayer('cow')
  summons: Summon[] = []; waves: Wave[] = []; enemies: Enemy[] = []; orbs: Orb[] = []; events: GameEvent[] = []
  choices: UpgradeId[] = []
  constructor(public random: () => number = Math.random) {}
  private createPlayer(id: CharacterId) {
    const c = CHARACTERS[id]
    return { x: 0, z: 0, vx: 0, vz: 0, hp: c.hp as number, maxHp: c.hp as number, speed: c.speed as number, damage: c.damage as number, radius: c.radius as number, attackTempo: c.tempo as number, knockback: c.knockback as number, angle: 0, facingX: 0, facingZ: -1, invuln: 0, skillCd: 0, cooldownScale: 1, skillActive: 0, charge: 0, dashX: 0, dashZ: -1, magnet: 3.5, attackCd: 0, attackPose: 0, abilityLevel: 1, traitLevel: 0, dashCd: 0, dashActive: 0 }
  }
  start(character: CharacterId, mode: Mode) {
    this.character = character; this.mode = mode; this.state = 'playing'
    this.distance = 0; this.runCoins = 0; this.summons = []; this.time = 0; this.kills = 0; this.level = 1; this.exp = 0; this.nextExp = 6
    this.combo = 0; this.maxCombo = 0; this.comboTimer = 0; this.spawnTimer = .4; this.id = 0
    this.bossSpawned = false; this.bossDefeated = false; this.nextBossAt = 90; this.previousSkill = false
    this.enemies = []; this.orbs = []; this.waves = []; this.events = []; this.choices = []
    this.player = this.createPlayer(character); this.clearInput()
    for (let i = 0; i < 5; i++) this.spawn(i === 4 ? 'fast' : 'normal')
  }
  clearInput() { this.input = { x: 0, z: 0, skill: false }; this.previousSkill = false; this.player.charge = 0 }
  pause() { if (this.state === 'playing') { this.state = 'paused'; this.clearInput() } }
  resume() { if (this.state === 'paused') { this.state = 'playing'; this.clearInput() } }
  drainEvents() { const events = this.events; this.events = []; return events }
  private event(type: GameEvent['type'], x = this.player.x, z = this.player.z, value?: number) { this.events.push({ type, x, z, value }) }
  spawn(type: EnemyType, x?: number, z?: number) {
    const a = this.random() * Math.PI * 2
    const c = ENEMIES[type]
    const px = x ?? this.player.x + Math.sin(a) * 15
    const pz = z ?? this.player.z + Math.cos(a) * 15
    const enemy: Enemy = { id: this.id++, type, x: px, z: pz, ...c, maxHp: c.hp, kx: 0, kz: 0, hitCd: 0, flash: 0, slow: 0, stun: 0, attack: 5 }
    const difficulty = Math.min(4, 1 + Math.max(0, this.time - 90) / 180)
    enemy.hp = enemy.maxHp = Math.round(enemy.hp * difficulty)
    enemy.speed *= Math.min(1.6, 1 + Math.max(0, this.time - 90) / 600)
    this.enemies.push(enemy)
    return enemy
  }
  step(dt: number) {
    if (this.state !== 'playing') return
    // Substeps prevent tunnelling; hidden-tab time is intentionally not caught up.
    let remaining = Math.min(Math.max(dt, 0), .1)
    while (remaining > .000001 && this.state === 'playing') {
      const step = Math.min(remaining, 1 / 60); remaining -= step; this.integrate(step)
    }
  }
  private integrate(dt: number) {
    const p = this.player
    this.time += dt
    p.invuln = Math.max(0, p.invuln - dt); p.skillCd = Math.max(0, p.skillCd - dt)
    p.skillActive = Math.max(0, p.skillActive - dt); p.dashActive = Math.max(0,p.dashActive-dt); p.dashCd = Math.max(0,p.dashCd-dt)
    p.attackCd = Math.max(0, p.attackCd - dt); p.attackPose = Math.max(0, p.attackPose - dt / (this.character === 'frog' ? .95 : .6))
    this.comboTimer -= dt
    if (this.comboTimer <= 0) this.combo = 0
    const mag = Math.hypot(this.input.x, this.input.z)
    const nx = this.input.x / Math.max(1, mag); const nz = this.input.z / Math.max(1, mag)
    if (mag > .12) { p.facingX = nx / Math.hypot(nx, nz); p.facingZ = nz / Math.hypot(nx, nz) }
    if (this.input.skill && !this.previousSkill && p.skillCd <= 0) this.activateSkill()
    this.previousSkill = this.input.skill
    const dash = this.character === 'cow' && p.dashActive > 0
    const speed = p.speed
    const response = this.character === 'cow' ? 7 : 17
    const ease = 1 - Math.exp(-response * dt)
    if (dash) { p.vx = p.dashX * (18+p.traitLevel); p.vz = p.dashZ * (18+p.traitLevel) }
    else { p.vx += (nx * speed - p.vx) * ease; p.vz += (nz * speed - p.vz) * ease }
    p.x += p.vx * dt; p.z += p.vz * dt; this.distance+=Math.hypot(p.vx,p.vz)*dt
    p.angle = Math.atan2(p.facingX,p.facingZ)
    this.spawnTimer -= dt
    if (this.spawnTimer <= 0) {
      this.spawnTimer += Math.max(.32, 1.35 - this.time * .008)
      if (this.enemies.length < 48) {
        const roll = this.random()
        this.spawn(this.time > 25 && roll < .2 ? 'heavy' : roll < .45 ? 'fast' : 'normal')
      }
    }
    if (this.mode === 'trial' && this.time >= 85 && !this.bossSpawned) {
      this.bossSpawned = true; this.spawn('boss'); this.event('boss')
    }
    if (this.mode === 'endless' && this.time >= this.nextBossAt && !this.bossSpawned) { this.bossSpawned = true; this.nextBossAt += 90; this.spawn('boss'); this.event('boss') }
    this.updateSummons(dt)
    this.autoAttack()
    for (const wave of this.waves) {
      wave.delay -= dt
      if (wave.delay > 0) continue
      const previousRadius = wave.radius; wave.radius += dt * 7
      for (const e of this.enemies) {
        const distance = Math.hypot(e.x - wave.x, e.z - wave.z)
        if (e.hp > 0 && !wave.hits.has(e.id) && distance <= wave.radius + e.radius && distance >= previousRadius - e.radius && distance <= wave.maxRadius + e.radius) {
          wave.hits.add(e.id); this.hitEnemy(e, wave.damage, 1.1, wave.x, wave.z)
        }
      }
    }
    this.waves = this.waves.filter(w => w.radius <= w.maxRadius)
    for (const e of this.enemies) {
      if (e.hp <= 0 || this.state !== 'playing') continue
      e.hitCd -= dt; e.flash = Math.max(0, e.flash - dt); e.slow = Math.max(0, e.slow - dt); e.stun = Math.max(0,e.stun-dt)
      const protector = this.summons.find(s=>s.kind==='mother'&&Math.hypot(e.x-s.x,e.z-s.z)<8)
      const dx = (protector?.x??p.x) - e.x; const dz = (protector?.z??p.z) - e.z; const distance = Math.hypot(dx, dz) || .001
      const blocked = Math.hypot(e.kx, e.kz) > 2
      const follow = blocked || e.stun > 0 ? 0 : e.speed * (e.slow > 0 ? .35 : 1)
      e.x += (dx / distance * follow + e.kx) * dt
      e.z += (dz / distance * follow + e.kz) * dt
      e.kx *= Math.exp(-7 * dt); e.kz *= Math.exp(-7 * dt)
      if (e.type === 'boss' && e.stun <= 0) {
        e.attack -= dt
        if (e.attack <= 0) {
          if (Math.hypot(e.x-p.x,e.z-p.z) < 4.5) this.hurt(30)
          e.attack = 5
        }
      }
      if (dash && Math.hypot(e.x-p.x,e.z-p.z) < p.radius + e.radius && e.hitCd <= 0) {
        this.hitEnemy(e, Math.round(p.damage * 2.5 + p.traitLevel*10), 2)
        e.hitCd = .3
      }
      if (e.hp > 0 && e.stun <= 0 && Math.hypot(e.x-p.x,e.z-p.z) < .55 + e.radius && !dash) this.hurt(e.damage)
    }
    this.enemies = this.enemies.filter(e => e.hp > 0 && (e.type==='boss'||Math.hypot(e.x-p.x,e.z-p.z)<42))
    for(const e of this.enemies) if(e.type==='boss'&&Math.hypot(e.x-p.x,e.z-p.z)>38){ e.x=p.x+18; e.z=p.z-8 }
    this.orbs = this.orbs.filter(o=>Math.hypot(o.x-p.x,o.z-p.z)<48)
    for (let i = this.orbs.length - 1; i >= 0 && this.state === 'playing'; i--) {
      const o = this.orbs[i]; const dx = p.x - o.x; const dz = p.z - o.z; const distance = Math.hypot(dx, dz)
      if (distance < 1.1) {
        this.exp += o.value; this.orbs.splice(i, 1); this.event('collect', o.x, o.z)
        if (this.exp >= this.nextExp) this.levelUp()
      } else if (distance < p.magnet) {
        const move = Math.min(distance, (5 + (p.magnet - distance) * 3) * dt)
        o.x += dx / distance * move; o.z += dz / distance * move
      }
    }
    if (this.state === 'playing' && this.mode === 'trial' && this.time >= 120 && this.bossDefeated) { this.state = 'won'; this.clearInput(); this.event('won') }
  }
  hurt(amount: number) {
    const p = this.player
    if (this.state !== 'playing' || p.invuln > 0) return
    p.hp = Math.max(0, p.hp - amount); p.invuln = .85; this.event('hurt', p.x, p.z, amount)
    if (p.hp === 0) { this.state = 'lost'; this.clearInput(); this.event('lost') }
  }
  hitEnemy(e: Enemy, damage: number, force = 1, originX = this.player.x, originZ = this.player.z) {
    if (e.hp <= 0 || this.state !== 'playing') return
    const p = this.player; const dx = e.x - originX; const dz = e.z - originZ; const d = Math.hypot(dx, dz) || 1
    e.hp -= damage; e.flash = .12
    const resistance = e.type === 'boss' ? .2 : e.type === 'heavy' ? .5 : 1
    e.kx += dx / d * p.knockback * force * resistance
    e.kz += dz / d * p.knockback * force * resistance
    this.combo++; this.comboTimer = 1.8; this.maxCombo = Math.max(this.combo, this.maxCombo)
    this.event('hit', e.x, e.z, damage)
    if (e.hp <= 0) {
      this.kills++; this.event('kill', e.x, e.z)
      const reward=e.type==='boss'?20:e.type==='heavy'?3:1;this.runCoins+=reward;this.event('coin',e.x,e.z,reward)
      if (this.orbs.length < 100) this.orbs.push({ id: this.id++, x: e.x, z: e.z, value: e.exp })
      else this.orbs[0].value += e.exp
      if (e.type === 'boss') {
        this.bossDefeated = true
        if (this.mode === 'endless') { this.bossSpawned = false; this.bossDefeated = false }
      }
    }
  }
  triggerDash() {
    const p=this.player
    if(this.state!=='playing'||this.character!=='cow'||p.dashCd>0)return false
    p.dashCd=Math.max(1.4,4-p.traitLevel*.35);p.dashActive=.34+p.traitLevel*.035
    const direction=Math.hypot(this.input.x,this.input.z);p.dashX=direction>.1?this.input.x/direction:p.facingX;p.dashZ=direction>.1?this.input.z/direction:p.facingZ;p.invuln=Math.max(p.invuln,p.dashActive+.15);this.event('dash');return true
  }
  triggerSkill(_charge = .5) {
    if (this.state !== 'playing' || this.player.skillCd > 0) return
    this.activateSkill()
  }
  private activateSkill() {
    const p = this.player; const rank=p.abilityLevel-1
    p.skillCd = CHARACTERS[this.character].cooldown * p.cooldownScale
    if (this.character === 'cow') {
      p.skillActive = .8; p.attackPose=1
      this.summons=[{id:this.id++,kind:'mother',x:p.x-1.6,z:p.z,life:6+rank*2,attackCd:.15,pose:0,facingX:0}]
      p.hp=Math.min(p.maxHp,p.hp+20+rank*5)
      if(p.abilityLevel>=2)this.summons.push({id:this.id++,kind:'leopard',x:p.x+1.6,z:p.z,life:6+rank*2,attackCd:.1,pose:0,facingX:0})
    } else if (this.character === 'frog') {
      p.skillActive = 1.5+rank*.18; p.attackPose = 1; p.invuln = .65
      for (let i = 0; i < 3+rank; i++) this.makeWave(35+rank*12, i * .2, 6+rank*.6)
    } else {
      p.skillActive = .85; p.attackPose = 1; p.invuln = .5
      for (const e of this.enemies) {
        if (Math.hypot(e.x - p.x, e.z - p.z) < 5+rank*.6) {
          this.hitEnemy(e, 35+rank*12, 1.7); e.slow = 3+rank*.4; e.stun=(2+rank*.4)*(e.type==='boss'?.4:1)
        }
      }
    }
    this.event('skill')
  }
  private updateSummons(dt:number) {
    const p=this.player
    for(const s of this.summons) {
      s.life-=dt;s.attackCd-=dt;s.pose=Math.max(0,s.pose-dt*2)
      let tx=p.x-1.8,tz=p.z-1
      let target:Enemy|undefined;let closest=12
      for(const e of this.enemies){const d=Math.hypot(e.x-s.x,e.z-s.z);if(e.hp>0&&d<closest){target=e;closest=d}}
      if(s.kind==='leopard'&&target&&Math.hypot(s.x-p.x,s.z-p.z)<14){tx=target.x;tz=target.z}
      const dx=tx-s.x,dz=tz-s.z,d=Math.hypot(dx,dz)
      if(d>.15){const move=Math.min(d,dt*(s.kind==='mother'?8:11));s.x+=dx/d*move;s.z+=dz/d*move;s.facingX=dx/d}
      if(Math.hypot(s.x-p.x,s.z-p.z)>20){s.x=p.x-2;s.z=p.z}
      if(s.attackCd<=0&&target) {
        const range=s.kind==='mother'?3.8:2.8
        if(Math.hypot(target.x-s.x,target.z-s.z)<range){
          s.attackCd=s.kind==='mother'?1.1:.55;s.pose=1
          for(const e of this.enemies) if(e.hp>0&&Math.hypot(e.x-s.x,e.z-s.z)<range&&(s.kind==='mother'||e===target))this.hitEnemy(e,(s.kind==='mother'?28:32)+(p.abilityLevel-1)*8,s.kind==='mother'?1.8:.7,s.x,s.z)
        }
      }
    }
    this.summons=this.summons.filter(s=>s.life>0)
  }
  private makeWave(damage: number, delay = 0, range = 4 + this.player.radius + this.player.traitLevel*.45) {
    if (this.waves.length >= 18) return
    this.waves.push({id:this.id++, x:this.player.x, z:this.player.z, radius:0, maxRadius:range, damage, delay, hits:new Set()})
  }
  private autoAttack() {
    const p = this.player
    if (p.attackCd > 0 || (this.character === 'cow' && p.dashActive > 0)) return
    const range = this.character === 'frog' ? 4 + p.radius + p.traitLevel*.45 : 1.6 + p.radius
    let target: Enemy | undefined; let closest = range + 1
    for (const e of this.enemies) {
      const d = Math.hypot(e.x-p.x,e.z-p.z)
      if (e.hp>0 && d < range+e.radius && d < closest) {target=e; closest=d}
    }
    if (!target) return
    if (closest > .001) {p.facingX=(target.x-p.x)/closest;p.facingZ=(target.z-p.z)/closest}
    const cadence = this.character === 'frog' ? 1.15 : this.character === 'cow' ? .8 : .95
    p.attackCd = Math.max(.32, cadence * CHARACTERS[this.character].tempo / p.attackTempo)
    p.attackPose = 1; this.event('attack')
    if (this.character === 'frog') { this.makeWave(p.damage+p.traitLevel*4); return }
    for (const e of this.enemies) {
      const dx=e.x-p.x; const dz=e.z-p.z; const d=Math.hypot(dx,dz)
      const facing = d < .001 ? 1 : (dx*p.facingX+dz*p.facingZ)/d
      if (e.hp>0 && d<range+e.radius && facing>-.05) {this.hitEnemy(e,p.damage,this.character==='cow'?1.3:1);e.hitCd=.3;if(this.character==='mouse'){e.slow=1.2+p.traitLevel*.4;e.stun=(.12+p.traitLevel*.08)*(e.type==='boss'?.4:1)}}
    }
    // A kangaroo sweeps the nearby flank with its tail as the kick lands.
    if (this.character === 'mouse') {
      for(const e of this.enemies) {
        const dx=e.x-p.x; const dz=e.z-p.z; const d=Math.hypot(dx,dz)
        if(e.hp>0 && d<1.3+p.radius && (dx*p.facingX+dz*p.facingZ)<0){this.hitEnemy(e,Math.round(p.damage*.65),1.2);e.slow=1.2+p.traitLevel*.4}
      }
      p.invuln=Math.max(p.invuln,.16)
    }
  }
  private levelUp() {
    this.exp -= this.nextExp; this.level++; this.nextExp = Math.min(60, this.nextExp + 4)
    this.state = 'upgrade'; this.clearInput(); this.event('level')
    const pool: UpgradeId[] = UPGRADES.map(u => u.id).filter(id => id !== 'skill' || this.player.cooldownScale > .250001)
    const unique:UpgradeId[]=[]
    if(this.player.abilityLevel<5)unique.push('ability')
    if(this.player.traitLevel<5)unique.push('trait')
    this.choices = []
    if(unique.length)this.choices.push(unique[Math.floor(this.random()*unique.length)])
    while(this.choices.length<3&&pool.length)this.choices.push(pool.splice(Math.floor(this.random()*pool.length),1)[0])
  }
  chooseUpgrade(id: UpgradeId) {
    if (this.state !== 'upgrade' || !this.choices.includes(id)) return false
    const p = this.player
    switch (id) {
      case 'ability': if(p.abilityLevel>=5)return false;p.abilityLevel++;break
      case 'trait': if(p.traitLevel>=5)return false;p.traitLevel++;break
      case 'damage': p.damage += 8; break
      case 'radius': p.radius += .2; break
      case 'speed': p.speed += .7; break
      case 'health': p.maxHp += 30; p.hp = Math.min(p.maxHp, p.hp + 50); break
      case 'magnet': p.magnet += 1.5; break
      case 'knockback': p.knockback += 4; break
      case 'spin': p.attackTempo = Math.min(32, p.attackTempo * 1.25); p.damage += 3; break
      case 'skill': p.cooldownScale = Math.max(.25, p.cooldownScale * .8); break
    }
    this.choices = []; this.state = 'playing'; this.clearInput(); if (this.exp >= this.nextExp) this.levelUp(); return true
  }
}
